import React from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { Card, Checkbox, CheckboxProps } from 'semantic-ui-react';
import { FaLightbulb } from 'react-icons/fa';
import styled from 'styled-components';

import { useAppSelector } from '@/store';
import { organizationAffiliationStore } from '@/store/organization/affiliation';
import { getFullName } from '@/utils/name';
import { Label } from '@/components/shared/Label';
import { isSubscriptionHodaiPlus } from '../util';

export type Props = {
  value: number[];
  error: string | (string | undefined)[];
  onChange: (data: CheckboxProps, adminId: number) => void;
  serviceGroupId: number;
};

export const Admins: React.FC<Props> = ({
  value,
  error,
  onChange,
  serviceGroupId,
}) => {
  const admins = useAppSelector(
    organizationAffiliationStore.selectors.activeAdmins,
  );
  const { t } = useTranslation();

  return (
    <Styled>
      <div id="adminIds" className={classnames('field', { error })}>
        <label>
          {isSubscriptionHodaiPlus(serviceGroupId)
            ? t('models.subscription.adminsAtHodaiPlus')
            : t('models.subscription.admins')}
        </label>
        {isSubscriptionHodaiPlus(serviceGroupId) && (
          <div className="date-picker">
            <div className="hint admin-reset with-icon">
              <FaLightbulb className="icon" />
              <div>
                {t('ui.expression.hodaiAdminFirstHalf')}
                <a
                  href={t('pages.support.hodaiAdmin.url')}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  {t('pages.support.hodaiAdmin.title')}
                </a>
                {t('ui.expression.hodaiAdminSecondHalf')}
              </div>
            </div>
          </div>
        )}
        <div className="admins">
          <Card.Group className="admin-cards">
            {admins.map((admin, i) => (
              <label
                data-test={`admin-${i}`}
                htmlFor={`admin-${admin.id}`}
                key={admin.id}
              >
                <Card
                  className={
                    value.includes(admin.id)
                      ? 'admin-card active'
                      : 'admin-card'
                  }
                >
                  <Card.Content className="admin-grid">
                    <Checkbox
                      id={`admin-${admin.id}`}
                      checked={value.includes(admin.id)}
                      onChange={(e, data) => onChange(data, admin.id)}
                    />
                    <Card.Header>
                      {getFullName(admin.firstName, admin.lastName)}
                    </Card.Header>
                    <Card.Meta>{admin.email}</Card.Meta>
                  </Card.Content>
                </Card>
              </label>
            ))}
          </Card.Group>
          <div className="hint with-icon">
            <FaLightbulb className="icon" />
            <span>
              {t('ui.expression.adminInvitationRequiredBeforeApplication')}
            </span>
          </div>
          {error && <Label>{error}</Label>}
        </div>
      </div>
    </Styled>
  );
};

const Styled = styled.div`
  .ui.cards.admin-cards {
    padding: 0.4325em 0;
    margin: 0 0 -1em;
    justify-content: space-between;

    label {
      display: block;
      width: 49%;
      margin-bottom: 1em !important;
    }
  }

  .ui.cards .ui.card.admin-card {
    width: 100%;
    border: 1px solid white;
  }

  .field#admins {
    margin: 0 0 3em;
  }

  .admin-grid > .checkbox {
    display: block;
    margin: 0.75em 1em 0 0.3em;
    float: left;
  }

  .ui.card > .content.admin-grid {
    padding: 1.5em 1em 1.25em;
    cursor: pointer;
  }

  .ui.card.active {
    border: 1px solid #2185d0 !important;
    background-color: #f1f9ff;
  }

  .ui.cards .ui.card.admin-card:hover {
    border-color: #8caff2;
    box-shadow: 0 0 1px #8caff2;
  }

  .date-picker {
    position: relative;
    width: 100%;
  }

  .date-picker > div {
    width: 49%;
  }

  .hint.admin-reset {
    width: 100%;
  }

  .with-icon {
    position: relative;
    padding-left: 18px;

    .icon {
      position: absolute;
      top: 3px;
      left: 1px;
    }
  }
`;
